import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authApi } from "../services/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "rocket_tronc_token";
const REFRESH_TOKEN_KEY = "rocket_tronc_refresh_token";
const USER_KEY = "rocket_tronc_user";

/**
 * Extract data from API response (handles both casing conventions)
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };
  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  const message = response.message ?? response.Message ?? null;
  return { success, data, message };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize from localStorage
  useEffect(() => {
    console.log("[AuthContext] Initializing from localStorage...");
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    console.log("[AuthContext] Stored token exists:", !!storedToken);
    console.log("[AuthContext] Stored user exists:", !!storedUser);

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log("[AuthContext] Restoring user:", parsedUser.email);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (err) {
        console.error("[AuthContext] Failed to parse stored user:", err);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    }
    setLoading(false);
    console.log("[AuthContext] Initialization complete");
  }, []);

  const login = useCallback(async (email, password) => {
    console.log("[AuthContext] Login attempt for:", email);
    setLoading(true);
    setError(null);

    try {
      const response = await authApi.login(email, password);
      const { success, data, message } = extractData(response);

      console.log("[AuthContext] Login response:", {
        success,
        hasData: !!data,
      });

      if (success && data) {
        console.log(
          "[AuthContext] Login successful, setting user:",
          data.user?.email,
        );

        // Save to localStorage IMMEDIATELY before setting state
        console.log("[AuthContext] Saving token to localStorage IMMEDIATELY");
        localStorage.setItem(TOKEN_KEY, data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));

        // Now set state
        setToken(data.accessToken);
        setUser(data.user);

        console.log("[AuthContext] User and token set successfully");
        return { success: true };
      } else {
        console.log("[AuthContext] Login failed:", message);
        setError(message || "Login failed");
        return { success: false, error: message || "Login failed" };
      }
    } catch (err) {
      console.error("[AuthContext] Login error:", err);
      const errorMessage = err.message || "Login failed";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    console.log("[AuthContext] Logging out...");
    try {
      await authApi.logout();
    } catch (err) {
      console.error("[AuthContext] Logout API error (ignoring):", err);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      console.log("[AuthContext] Logout complete");
    }
  }, []);

  const refreshToken = useCallback(async () => {
    console.log("[AuthContext] Attempting to refresh token...");
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!storedRefreshToken) {
      console.log("[AuthContext] No refresh token found");
      return false;
    }

    try {
      const response = await authApi.refresh(storedRefreshToken);
      const { success, data } = extractData(response);

      if (success && data) {
        console.log("[AuthContext] Token refreshed successfully");
        // Save to localStorage immediately
        localStorage.setItem(TOKEN_KEY, data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        setToken(data.accessToken);
        return true;
      }
    } catch (err) {
      console.error("[AuthContext] Token refresh failed:", err);
      // Refresh failed, logout
      await logout();
    }
    return false;
  }, [logout]);

  const updateUser = useCallback(
    (updatedUser) => {
      console.log("[AuthContext] Updating user:", updatedUser);
      const newUser = { ...user, ...updatedUser };
      setUser(newUser);
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    },
    [user],
  );

  // Check if user has a specific role
  const hasRole = useCallback(
    (role) => {
      if (!user) return false;
      if (Array.isArray(role)) {
        return role.includes(user.role);
      }
      return user.role === role;
    },
    [user],
  );

  // Check if user is a global admin
  const isGlobalAdmin = user?.role === "GlobalAdmin";

  // Check if user is at least a tenant admin
  const isTenantAdmin =
    user?.role === "TenantAdmin" || user?.role === "GlobalAdmin";

  // Check if user is a manager or higher
  const isManager =
    user?.role === "Manager" ||
    user?.role === "TenantAdmin" ||
    user?.role === "GlobalAdmin";

  // Check if user is a regular employee (User role)
  const isRegularUser = user?.role === "User";

  // Check if user is authenticated
  const isAuthenticated = !!token && !!user;

  // Log auth state changes
  useEffect(() => {
    console.log("[AuthContext] Auth state:", {
      isAuthenticated,
      hasToken: !!token,
      hasUser: !!user,
      userEmail: user?.email,
      userRole: user?.role,
      loading,
    });
  }, [isAuthenticated, token, user, loading]);

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isGlobalAdmin,
    isTenantAdmin,
    isManager,
    isRegularUser,
    login,
    logout,
    refreshToken,
    updateUser,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
